import { useCallback, useEffect, useRef, useState } from 'react';

// What the backend accepts, in the order we prefer it. Chrome and Firefox record webm/ogg
// (Opus), Safari records mp4 (AAC).
const MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/ogg'];

export const MAX_RECORDING_SECONDS = 60;

const pickMimeType = () => {
  if (typeof window === 'undefined' || !window.MediaRecorder) return null;
  if (typeof window.MediaRecorder.isTypeSupported !== 'function') return '';
  return MIME_TYPES.find((type) => window.MediaRecorder.isTypeSupported(type)) ?? null;
};

/**
 * Records a short dictation from the microphone.
 *
 *   supported   false when the browser cannot record (the mic button is then not shown)
 *   recording   true while recording; `seconds` counts up
 *   error       'denied' | 'failed' | null
 *   start()     asks for the microphone and starts
 *   stop()      stops; `onRecorded(blob)` is called with the recording
 *   cancel()    stops and throws the recording away
 *
 * Recording stops by itself at MAX_RECORDING_SECONDS. Nothing is kept after `onRecorded`.
 */
export default function useAudioRecorder({ onRecorded }) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const discardRef = useRef(false);
  const onRecordedRef = useRef(onRecorded);
  onRecordedRef.current = onRecorded;

  const supported = typeof navigator !== 'undefined'
    && !!navigator.mediaDevices?.getUserMedia
    && pickMimeType() !== null;

  const release = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    recorderRef.current = null;
    setRecording(false);
  }, []);

  const stop = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
  }, []);

  const cancel = useCallback(() => {
    discardRef.current = true;
    stop();
  }, [stop]);

  const start = useCallback(async () => {
    if (recorderRef.current) return;
    setError(null);
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      setError(e?.name === 'NotAllowedError' || e?.name === 'SecurityError' ? 'denied' : 'failed');
      return;
    }
    try {
      const mimeType = pickMimeType();
      const recorder = mimeType ? new window.MediaRecorder(stream, { mimeType }) : new window.MediaRecorder(stream);
      streamRef.current = stream;
      recorderRef.current = recorder;
      chunksRef.current = [];
      discardRef.current = false;
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const type = recorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type });
        chunksRef.current = [];
        const discard = discardRef.current;
        release();
        if (!discard && blob.size > 0) onRecordedRef.current?.(blob);
      };
      recorder.onerror = () => {
        discardRef.current = true;
        setError('failed');
        release();
      };
      recorder.start();
      setSeconds(0);
      setRecording(true);
      timerRef.current = setInterval(() => {
        setSeconds((value) => {
          if (value + 1 >= MAX_RECORDING_SECONDS) stop();
          return value + 1;
        });
      }, 1000);
    } catch (e) {
      stream.getTracks().forEach((track) => track.stop());
      setError('failed');
      release();
    }
  }, [release, stop]);

  // Leaving the window while recording must switch the microphone off.
  useEffect(() => () => {
    discardRef.current = true;
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
    streamRef.current?.getTracks().forEach((track) => track.stop());
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  return { supported, recording, seconds, error, start, stop, cancel, clearError: () => setError(null) };
}
